<template>
  <component
    :is="tag"
    ref="cellRef"
    :class="classes"
    :tabindex="tab"
    contenteditable="false"
    @blur="blurCell"
    @click="clickDescription"
    @keydown="keydownCell($event)"
    @input="handleInput"
    @paste="handleInput"
    @cut="handleInput"
    :style="{ backgroundColor: color || '' }"
    v-text="value"
  />
</template>

<script setup>
import { computed, defineExpose, ref } from 'vue'
import { useStore } from 'vuex'
import inputHelpers from '@/helpers/inputHelpers'

const props = defineProps({
  cell: Boolean,
  verb: Boolean,
  isReadModel: Boolean,
  header: Boolean,
  headerAfterVerb: Boolean,
  list: Boolean,
  tab: Number,
  tag: {
    type: String,
    default: 'td'
  },
  value: String,
  color: String
})

const emit = defineEmits(['blur-table-cell', 'leave-cell', 'update:value'])

const store = useStore()
const cellRef = ref(null)
let hasBeenEdited = false
let cachedValue = null

const classes = computed(() => ({
  'btc-cell': props.cell,
  'list-cell': props.list,
  'btc-header-cell': props.header,
  'btc-header-cell-verb': props.verb,
  'btc-header-after-verb': props.headerAfterVerb,
  'bg-green-lighten-4': props.isReadModel
}))

const cacheOriginalValue = (target) => {
  cachedValue = (target.innerText || '').trim()
  hasBeenEdited = false
}

const handleInput = () => {
  hasBeenEdited = true
}

const restoreValue = (event, value) => {
  if (event.target.isContentEditable) {
    event.target.innerText = value
    inputHelpers.moveCursorToTextEnd(event.target)
  }
}

const isPrintableCharacter = (keycode) => {
  return (keycode > 47 && keycode < 58) ||
    keycode === 32 || keycode === 13 ||
    (keycode > 64 && keycode < 91) ||
    (keycode > 95 && keycode < 112) ||
    (keycode > 185 && keycode < 193) ||
    (keycode > 218 && keycode < 223)
}

const blurCell = (event) => {
  if (event.target.innerText.length > 256) {
    alert('Data field must be max 256 characters')
    restoreValue(event, props.value)
    event.target.setAttribute('contenteditable', 'false')
    hasBeenEdited = false
    cachedValue = null

    return
  }

  event.target.setAttribute('contenteditable', 'false')

  const currentValue = (event.target.innerText || '').trim()
  const originalValue = cachedValue !== null ? cachedValue : (props.value || '').trim()

  if (hasBeenEdited || currentValue !== originalValue) {
    emit('blur-table-cell', event)
  }

  hasBeenEdited = false
  cachedValue = null
}

const clickDescription = (event) => {
  if (store.state.isWorkflowDisabled === false) {
    const contenteditable = event.target.getAttribute('contenteditable')
    if (contenteditable !== 'true') {
      cacheOriginalValue(event.target)
      event.target.setAttribute('contenteditable', 'true')
      event.target.focus()
    }
  }
}

const keydownCell = (event) => {
  const isContentEditable = event.target.getAttribute('contenteditable') === 'true'

  switch (event.keyCode) {
    case 13: // enter
      if (isContentEditable) {
        if (event.altKey && props.cell) {
          // Alt+Enter (Option+Enter on Mac) = insert newline (only for cell type, not headers)
          event.preventDefault()
          event.stopPropagation()
          const existingBrs = event.target.querySelectorAll('br')
          const shouldInsertTwo = existingBrs.length === 0

          const selection = window.getSelection()
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            range.deleteContents()

            if (shouldInsertTwo) {
              const br1 = document.createElement('br')
              const br2 = document.createElement('br')
              range.insertNode(br1)
              range.setStartAfter(br1)
              range.collapse(true)
              range.insertNode(br2)
              range.setStartAfter(br2)
              range.collapse(true)
            } else {
              const br = document.createElement('br')
              range.insertNode(br)
              range.setStartAfter(br)
              range.collapse(true)
            }

            selection.removeAllRanges()
            selection.addRange(range)
          }
          hasBeenEdited = true

          return
        } else {
          // Regular Enter = leave cell
          emit('leave-cell', event)
        }
      } else {
        cacheOriginalValue(event.target)
        event.target.setAttribute('contenteditable', 'true')
        event.target.focus()
        inputHelpers.moveCursorToTextEnd(event.target)
        event.preventDefault()
      }
      break
    case 27: // escape
      restoreValue(event, props.value)
      hasBeenEdited = false
      cachedValue = null
      break
    case 37: // left
    case 38: // up
    case 39: // right
    case 40: // down
      if (!isContentEditable) {
        emit('leave-cell', event)
      }
      break
    default: {
      const isPrintableNonModifier = isPrintableCharacter(event.keyCode) && !event.altKey && !event.metaKey

      if (isContentEditable && isPrintableNonModifier) {
        hasBeenEdited = true
      }
      if (!store.state.isWorkflowDisabled && !isContentEditable && isPrintableNonModifier) {
        cacheOriginalValue(event.target)
        event.target.setAttribute('contenteditable', 'true')
        event.target.innerText = ''
        event.target.focus()
        inputHelpers.moveCursorToTextEnd(event.target)
        hasBeenEdited = true
      }
    }
  }
  event.stopPropagation()
}

const setFocus = () => {
  cellRef.value?.focus()
}

defineExpose({ setFocus })
</script>

<style>
div {
  outline: none !important;
}

.btc-cell {
  font-size: 14px !important;
  padding: 9px 16px !important;
  border-right: 1px solid #E5E3E1;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.list-cell {
  &::before {
    content: 'List: ';
    font-size: 12px;
    color: #9e9e9e;
    vertical-align: bottom;
  }
}

td.btc-cell.orange.lighten-5 {
  border-color: rgba(0,0,0,.12);
  border-bottom: thin solid rgba(0,0,0,.12) !important;
}

[data-theme="dark"] .btc-cell {
  border-right-color: var(--light-3);
}

[data-theme="dark"] td.btc-cell.orange.lighten-5 {
  border-bottom-color: var(--light-3) !important;
}

.btc-header-cell {
  width: 100%;
  height: 100%;
  font-size: 16px;
  font-weight: 500;
  line-height: 36px;
  padding: 8px 12px;
  background-color: var(--blue-2);
  color: var(--text-dark, #1F2833);
  white-space: nowrap;
}

.btc-header-after-verb{
  height: 100%;
  font-size: 16px;
  font-weight: 500;
  line-height: 16px;
  padding: 4px 12px;
  background-color: var(--blue-2);
  color: var(--text-dark, #1F2833);
}

.btc-header-cell-verb {
  height: 100%;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  padding: 14px 12px 4px;
  background-color: var(--blue-2);
  color: var(--text-light, #4F5359);
}
</style>
